import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle, FileText, Scale, Gavel, ArrowRight, CheckCircle2, Clock,
  ShieldAlert, ChevronDown, Search, MessageSquare, Send, Calendar, Users,
} from "lucide-react";

/**
 * Visually richer extension of the Appeals & Refusals service page.
 * Rendered after the standard ServicePage sections when slug === "appeals".
 * All content is original StudyNet writing based on publicly available
 * Department of Home Affairs and Administrative Review Tribunal information.
 */

const REFUSAL_REASONS = [
  { icon: FileText, title: "Genuine Student / Genuine Temporary Entrant refusals",
    desc: "Subclass 500 refusals citing weak study plan, inconsistent course history, unclear career outcome or insufficient ties to your home country." },
  { icon: Users, title: "Partner & family refusals",
    desc: "Subclass 309/100, 820/801, prospective marriage and parent visa refusals — typically on relationship genuineness, sponsorship or document evidence grounds." },
  { icon: Scale, title: "Sponsorship & nomination refusals",
    desc: "Subclass 482 / 186 / 494 employer-related refusals — Annual Market Salary Rate, position genuineness, Labour Market Testing, or sponsor capacity." },
  { icon: AlertCircle, title: "Skilled visa refusals",
    desc: "GSM (189 / 190 / 491) refusals on points claims, English evidence, skills assessment, age or occupation list issues." },
  { icon: ShieldAlert, title: "Character & cancellation matters",
    desc: "Refusals or cancellations under section 501 of the Migration Act 1958, including substantial criminal record or character test concerns." },
  { icon: Gavel, title: "Visa cancellations & Bridging Visa E",
    desc: "Cancellation of a substantive visa, breach of conditions, or non-compliance issues — often paired with Bridging Visa E status." },
];

const PROCESS_STEPS = [
  { icon: Search, title: "Free initial review",
    desc: "We review your refusal letter and identify whether you have review rights and which forum (ART, MRD, AAT-legacy or Federal Court) applies to your matter." },
  { icon: FileText, title: "Strategy & scope",
    desc: "A written merits assessment with a clear recommendation: appeal, ministerial intervention, fresh application, judicial review, or stop." },
  { icon: Send, title: "Lodge within deadline",
    desc: "We prepare and lodge your application for review within the strict 7 / 21 / 28 / 35-day deadlines that apply to your visa class." },
  { icon: MessageSquare, title: "Evidence & submissions",
    desc: "Targeted new evidence packs, expert reports where useful, and written submissions addressing the original refusal reasons head-on." },
  { icon: Calendar, title: "Hearing representation",
    desc: "Full preparation for your Tribunal hearing — narrative outline, witness coordination, document bundle and mock questioning." },
  { icon: CheckCircle2, title: "Decision & next steps",
    desc: "Whether the Tribunal sets the original decision aside, remits it for re-determination or affirms it, we map out your next safe move." },
];

const FAQS = [
  { q: "How long do I have to lodge an appeal?",
    a: "Review deadlines are strict and depend on your visa class and location. Many visa refusals carry a 21-day deadline if you are in Australia, 28 days if offshore, and as little as 7 working days for certain bridging-visa or detention matters. For judicial review at the Federal Circuit and Family Court, the deadline is generally 35 days from notification of the Tribunal's decision. Missing the deadline almost always extinguishes your right of review — act on the day you receive your refusal letter." },
  { q: "What happens to my immigration status while my appeal is pending?",
    a: "Onshore applicants will usually be granted a Bridging Visa for the duration of the review. The conditions of that bridging visa (work, study and travel rights) depend on the substantive visa you held immediately before lodging the review application. Offshore applicants do not receive a bridging visa and remain reliant on their current home-country status." },
  { q: "Is the Administrative Review Tribunal (ART) a court?",
    a: "No. The ART is an independent merits-review body that replaced the Administrative Appeals Tribunal (AAT) from late 2024. It conducts new fact-finding on the merits — meaning the Tribunal can substitute a new decision in place of the Department's — but it is not a court. Judicial review at the Federal Circuit and Family Court is a separate, narrower process limited to jurisdictional error." },
  { q: "Can I lodge new evidence with the Tribunal that wasn't before the original decision-maker?",
    a: "Yes. The Tribunal is generally not limited to the evidence that was before the Department. Strong appeals typically introduce new evidence — updated relationship documents, expert reports, new financial records, additional employment evidence, or updated character documents — that directly addresses the refusal reasons." },
  { q: "How much does an appeal cost?",
    a: "There are two cost components. The Tribunal application fee is set by the Commonwealth and changes from time to time — a partial refund is available if the original decision is set aside. Our professional fees are quoted in writing after the initial merits review so you know the full scope and cost before any engagement." },
  { q: "How long does an ART appeal take?",
    a: "Listing times vary significantly by visa type and current Tribunal caseload, ranging from several months to 24+ months. Onshore Student, Partner and Family appeals have historically faced longer queues. We monitor your matter throughout and respond promptly to any Tribunal requests, which is often what keeps a case moving." },
  { q: "What is Ministerial intervention and when is it useful?",
    a: "Ministerial intervention is a discretionary, last-resort pathway after the Tribunal has affirmed a refusal or where the case otherwise reaches a dead end. The Minister is not obliged to consider any request, and intervention guidelines require unique or exceptional circumstances — humanitarian, compelling personal, or strong public-interest factors. We will only recommend this where the facts genuinely justify it." },
  { q: "Can I lodge a fresh application instead of appealing?",
    a: "Sometimes — and sometimes it's strategically the better path. The decision turns on bar status (e.g. Schedule 3, section 48), the strength of the original refusal reasons and any new evidence available. A consultation will help you compare the appeal pathway with a fresh-application pathway side by side." },
];

function FaqItem({ item, isOpen, onToggle, idx }) {
  return (
    <div data-testid={`appeals-faq-${idx}`} className="bg-white border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`appeals-faq-toggle-${idx}`}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-4 text-left px-5 md:px-6 py-4 hover:bg-[#F8F4FB] transition-colors"
      >
        <span className="font-display text-[15px] md:text-base text-[#29324A] leading-snug">{item.q}</span>
        <ChevronDown className={`h-5 w-5 text-[#C43A89] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="px-5 md:px-6 pb-5 pt-1 border-t border-slate-100 text-[13px] text-slate-700 leading-relaxed">
            {item.a}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppealsExtras() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      {/* Refusal reasons grid */}
      <section data-testid="appeals-refusal-reasons" className="py-16 md:py-20 bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">
              The refusals we routinely handle
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight">
              Every refusal letter tells a different story.
            </h2>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Whether the decision turns on documents, evidence, character or strategy, our Registered Migration Agents diagnose the real issue before recommending a path forward.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {REFUSAL_REASONS.map((r, i) => {
              const Icon = r.icon;
              return (
                <div
                  key={i}
                  data-testid={`refusal-reason-${i}`}
                  className="group bg-white border border-slate-200 hover:border-[#C43A89] transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(196,58,137,0.18)] p-6 md:p-7"
                >
                  <span aria-hidden className="block h-0.5 w-10 bg-[#C43A89] mb-5 group-hover:w-full transition-all duration-500" />
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white flex items-center justify-center shadow-md mb-4">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-lg text-[#29324A] mb-2 leading-snug">{r.title}</h3>
                  <p className="text-[13px] text-slate-600 leading-relaxed">{r.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process / step-by-step timeline */}
      <section data-testid="appeals-process" className="py-16 md:py-20 bg-[#F8F4FB] border-t border-slate-200 relative overflow-hidden">
        <div aria-hidden className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#C43A89]/8 blur-3xl" />
        <div aria-hidden className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-[#29324A]/8 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">
              Our appeal process
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight">
              How we take you from refusal letter to a decision you can act on.
            </h2>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed">
              A structured, transparent process — every step has a clear deliverable, fixed fee scope and a written record you keep.
            </p>
          </div>

          <ol className="relative grid md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-3">
            <span aria-hidden className="hidden md:block absolute top-7 left-[5%] right-[5%] h-px bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
            {PROCESS_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <li
                  key={i}
                  data-testid={`appeals-step-${i}`}
                  className="relative bg-white border border-slate-200 p-5 md:p-6 hover:border-[#C43A89] transition-colors group flex flex-col"
                >
                  <span aria-hidden className="absolute top-0 left-0 h-0.5 w-8 bg-[#C43A89] group-hover:w-full transition-all duration-500" />
                  <div className="relative z-10 h-14 w-14 rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white flex items-center justify-center shadow-lg mb-4">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-1">Step {i + 1}</div>
                  <div className="font-display text-base text-[#29324A] leading-snug mb-2">{s.title}</div>
                  <p className="text-[12.5px] text-slate-600 leading-relaxed">{s.desc}</p>
                </li>
              );
            })}
          </ol>

          {/* Critical-deadlines reminder */}
          <div className="mt-10 bg-white border-l-4 border-[#C43A89] p-5 md:p-6 flex items-start gap-3">
            <Clock className="h-5 w-5 text-[#C43A89] shrink-0 mt-0.5" />
            <div className="text-[13px] text-slate-700 leading-relaxed">
              <strong className="text-[#29324A]">Time-critical:</strong> Visa-refusal review deadlines are typically <strong>7, 21 or 28 days</strong> from notification — and almost never extendable. The first 72 hours after receiving a refusal letter usually shape the entire outcome. <Link to="/book-consultation" className="text-[#29324A] underline font-semibold">Book a consultation today</Link>.
            </div>
          </div>
        </div>
      </section>

      {/* What happens at the Tribunal */}
      <section data-testid="appeals-tribunal" className="py-16 md:py-20 bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">
                Inside the Tribunal
              </div>
              <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mb-4">
                What an ART hearing actually looks like.
              </h2>
              <p className="text-sm text-slate-700 leading-relaxed mb-5">
                Most visa-refusal hearings are conducted in person at an ART registry, by video link or by telephone. The Tribunal Member typically sits with the applicant, the representative and an interpreter (where requested), and the hearing is recorded.
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                You will be asked questions under oath or affirmation, the Member will probe the issues raised in the original refusal, and your representative will make submissions in support. Hearings can run from one hour to a full day depending on complexity.
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              <div className="bg-[#F8F4FB] border-l-4 border-[#29324A] p-5">
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#29324A] uppercase mb-1">Possible outcomes</div>
                <ul className="mt-2 space-y-1.5 text-[13px] text-slate-700">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" /> Decision set aside — Tribunal substitutes a different decision (e.g. grant).</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" /> Remitted to the Department for re-consideration with directions.</li>
                  <li className="flex items-start gap-2"><AlertCircle className="h-3.5 w-3.5 mt-0.5 text-rose-600 shrink-0" /> Decision affirmed — refusal stands; further pathways may include judicial review or ministerial intervention.</li>
                </ul>
              </div>
              <div className="bg-white border border-slate-200 p-5">
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-1">Hearing essentials</div>
                <ul className="mt-2 space-y-1.5 text-[13px] text-slate-700">
                  <li className="flex items-start gap-2"><Users className="h-3.5 w-3.5 mt-0.5 text-[#29324A] shrink-0" /> Free interpreter service available — request in advance.</li>
                  <li className="flex items-start gap-2"><FileText className="h-3.5 w-3.5 mt-0.5 text-[#29324A] shrink-0" /> Bring originals of all key documents.</li>
                  <li className="flex items-start gap-2"><MessageSquare className="h-3.5 w-3.5 mt-0.5 text-[#29324A] shrink-0" /> Be ready to explain inconsistencies — directly and briefly.</li>
                  <li className="flex items-start gap-2"><Clock className="h-3.5 w-3.5 mt-0.5 text-[#29324A] shrink-0" /> Decisions are usually reserved and posted within a few weeks.</li>
                </ul>
              </div>
              <div className="sm:col-span-2 bg-[#29324A] text-white p-5 grain relative">
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-1">Beyond the Tribunal</div>
                <p className="text-[13px] leading-relaxed mt-1">
                  If the Tribunal affirms the refusal, the next legal options are <strong>judicial review</strong> at the Federal Circuit and Family Court (within 35 days, jurisdictional error only) or, in narrow circumstances, a <strong>Ministerial intervention</strong> request based on unique or exceptional personal circumstances.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="appeals-faq" className="py-16 md:py-20 bg-[#F8F9FA] border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center justify-center gap-2">
              <AlertCircle className="h-3.5 w-3.5" /> Frequently asked questions
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight">
              Visa appeals — your questions, answered.
            </h2>
          </div>

          <div className="space-y-3">
            {FAQS.map((item, i) => (
              <FaqItem
                key={i}
                idx={i}
                item={item}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>

          {/* Final CTA */}
          <div className="mt-10 bg-[#29324A] text-white p-6 md:p-8 grain relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">Refusal letter in hand?</div>
              <h3 className="font-display text-xl md:text-2xl leading-snug">
                Bring it to our team — the first 72 hours matter most.
              </h3>
            </div>
            <Link
              to="/book-consultation"
              data-testid="appeals-final-cta"
              className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors btn-shimmer"
            >
              Book consultation <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
